import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "..";
import { Icon } from "@iconify/react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const InvoiceListLayer = ({
  jobsData,
  pagination = DEFAULT_PAGINATION,
  onPageChange,
  onLimitChange,
  loading,
  searchTerm,
  onSearchTermChange,
  onSearchSubmit,
  onRefresh,
}) => {
  const history = useNavigate();

  const normalizeStatus = (status) => {
    return String(status || "").toLowerCase().trim();
  };

  const isInvoiceOverdue = (invoice) => {
    const status = normalizeStatus(invoice?.status);

    if (status === "paid") return false;
    if (!invoice?.due_date) return false;

    const dueDate = new Date(invoice.due_date);

    if (Number.isNaN(dueDate.getTime())) return false;

    const today = new Date();

    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const getEffectiveStatus = (invoice) => {
    const status = normalizeStatus(invoice?.status);

    if (status === "paid") return "paid";
    if (isInvoiceOverdue(invoice)) return "overdue";

    return "unpaid";
  };

  const getRowClass = (invoice) => {
    const effectiveStatus = getEffectiveStatus(invoice);

    if (effectiveStatus === "paid") return "custom-invoice-row invoice-row-paid";
    if (effectiveStatus === "overdue")
      return "custom-invoice-row invoice-row-overdue";

    return "custom-invoice-row invoice-row-unpaid";
  };

  const getMobileCardClass = (invoice) => {
    const effectiveStatus = getEffectiveStatus(invoice);

    if (effectiveStatus === "paid")
      return "custom-invoice-card invoice-row-paid";
    if (effectiveStatus === "overdue")
      return "custom-invoice-card invoice-row-overdue";

    return "custom-invoice-card invoice-row-unpaid";
  };

  const formatDateString = (dateStr) => {
    if (!dateStr) return "N/A";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) return "N/A";

    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    return date.toLocaleDateString("en-GB", options);
  };

  const formatMoney = (amount) => {
    return Number(amount || 0).toFixed(2);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete invoice?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/invoice/delete-invoice/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.message === "Invoice deleted successfully") {
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/invoice/update-status/${id}`, {
        method: "PUT",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
        }),
      });

      const result = await response.json();

      if (result.message === "Invoice successfully updated") {
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const { page = 1, limit = 25, total = 0, totalPages = 1 } = pagination || {};

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * limit, total);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const windowSize = 1;

    const addPage = (p) => pages.push(p);
    const addEllipsis = () => pages.push("...");

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) addPage(i);
      return pages;
    }

    addPage(1);

    if (page - windowSize > 2) {
      addEllipsis();
    }

    const start = Math.max(2, page - windowSize);
    const end = Math.min(totalPages - 1, page + windowSize);

    for (let i = start; i <= end; i++) {
      addPage(i);
    }

    if (page + windowSize < totalPages - 1) {
      addEllipsis();
    }

    addPage(totalPages);

    return pages;
  }, [page, totalPages]);

  const TooltipButton = ({ tooltip, className, icon, onClick }) => {
    return (
      <OverlayTrigger
        placement="top"
        delay={{ show: 180, hide: 80 }}
        overlay={
          <Tooltip id={`invoice-tooltip-${tooltip.replace(/\s+/g, "-")}`}>
            {tooltip}
          </Tooltip>
        }
      >
        <span className="invoice-action-tooltip-wrap">
          <button
            type="button"
            className={`invoice-action-btn ${className}`}
            aria-label={tooltip}
            onClick={onClick}
          >
            <Icon icon={icon} />
          </button>
        </span>
      </OverlayTrigger>
    );
  };

  const renderActions = (invoice) => {
    return (
      <div className="invoice-action-bar">
        <TooltipButton
          tooltip="View Invoice"
          className="view"
          icon="ph:eye"
          onClick={() => history(`/invoices/preview/${invoice.id}`)}
        />

        <TooltipButton
          tooltip="Edit Invoice"
          className="edit"
          icon="ph:pencil-simple-line"
          onClick={() => history(`/invoices/edit/${invoice.id}`)}
        />

        <TooltipButton
          tooltip="Delete Invoice"
          className="delete"
          icon="ph:trash"
          onClick={() => handleDelete(invoice.id)}
        />
      </div>
    );
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && onSearchSubmit) {
      onSearchSubmit();
    }
  };

  return (
    <>
      <style>
        {`
          .custom-invoice-table-card {
            border: 1px solid #e8eef7;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
          }

          .custom-invoice-table-card .card-header {
            border-bottom: 1px solid #edf2f7;
            background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
            padding: 18px 20px;
          }

          .invoice-table-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
          }

          .invoice-search-box {
            display: flex;
            align-items: center;
            gap: 8px;
            width: min(100%, 560px);
          }

          .invoice-search-box input {
            min-height: 42px;
          }

          .invoice-search-btn {
            min-height: 42px;
            border: 0;
            border-radius: 10px;
            padding: 0 16px;
            background: #2563eb;
            color: #ffffff;
            font-weight: 700;
            cursor: pointer;
          }

          .invoice-table-scroll {
            width: 100%;
            overflow-x: auto;
            border: 1px solid #e8eef7;
            border-radius: 14px;
          }

          .custom-invoice-table {
            width: 100%;
            min-width: 1080px;
            margin: 0;
            border-collapse: separate;
            border-spacing: 0;
          }

          .custom-invoice-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f8fafc;
            color: #334155;
            font-size: 12px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            border-bottom: 1px solid #e2e8f0;
            padding: 13px 14px;
            white-space: nowrap;
          }

          .custom-invoice-table tbody td {
            padding: 13px 14px;
            border-bottom: 1px solid rgba(226, 232, 240, 0.8);
            vertical-align: middle;
            font-size: 14px;
            color: #334155;
          }

          .custom-invoice-table tbody tr:last-child td {
            border-bottom: none;
          }

          .custom-invoice-row {
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }

          .custom-invoice-row:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          }

          .custom-invoice-table tbody tr.invoice-row-paid td,
          .custom-invoice-card.invoice-row-paid {
            background: #dcfce7 !important;
          }

          .custom-invoice-table tbody tr.invoice-row-unpaid td,
          .custom-invoice-card.invoice-row-unpaid {
            background: #fef3c7 !important;
          }

          .custom-invoice-table tbody tr.invoice-row-overdue td,
          .custom-invoice-card.invoice-row-overdue {
            background: #fee2e2 !important;
            color: #7f1d1d;
          }

          .invoice-id-link {
            border: 0;
            background: transparent;
            padding: 0;
            color: #2563eb;
            font-weight: 800;
            text-decoration: underline;
            cursor: pointer;
          }

          .invoice-number-link {
            border: 0;
            background: transparent;
            padding: 0;
            color: #0f172a;
            font-weight: 800;
            text-decoration: underline;
            cursor: pointer;
          }

          .invoice-customer-cell {
            display: inline-block;
            max-width: 220px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: 700;
            color: #0f172a;
          }

          .invoice-muted-cell {
            color: #64748b;
          }

          .invoice-amount-cell {
            font-weight: 800;
            color: #0f172a;
          }

          .invoice-date-cell {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .invoice-overdue-date-label {
            display: inline-flex;
            padding: 3px 8px;
            border-radius: 999px;
            background: #991b1b;
            color: #ffffff;
            font-size: 11px;
            font-weight: 800;
          }

          .invoice-status-cell {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .status-dropdown {
            min-width: 105px;
            padding: 6px 10px;
            border-radius: 8px;
            border: 1px solid #d1d5db;
            background-color: #ffffff;
            color: #111827;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
          }

          .invoice-status-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 5px 10px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 800;
            line-height: 1;
            text-transform: capitalize;
          }

          .invoice-status-badge.paid {
            background: #16a34a;
            color: #ffffff;
          }

          .invoice-status-badge.unpaid {
            background: #f59e0b;
            color: #111827;
          }

          .invoice-status-badge.overdue {
            background: #dc2626;
            color: #ffffff;
          }

          .invoice-action-bar {
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            gap: 6px;
            white-space: nowrap;
          }

          .invoice-action-tooltip-wrap {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 0;
          }

          .invoice-action-btn {
            width: 32px;
            height: 32px;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #ffffff;
            color: #334155 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 17px;
            cursor: pointer;
            transition: all 0.15s ease;
            box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
          }

          .invoice-action-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
          }

          .invoice-action-btn.view {
            background: #eff6ff;
            border-color: #bfdbfe;
            color: #2563eb !important;
          }

          .invoice-action-btn.edit {
            background: #f5f3ff;
            border-color: #ddd6fe;
            color: #7c3aed !important;
          }

          .invoice-action-btn.delete {
            background: #fef2f2;
            border-color: #fecaca;
            color: #dc2626 !important;
          }

          .custom-invoice-mobile-list {
            display: none;
          }

          .custom-invoice-card {
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 14px;
            margin-bottom: 12px;
          }

          .invoice-mobile-top {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 10px;
          }

          .invoice-mobile-title {
            font-weight: 800;
            color: #0f172a;
            font-size: 15px;
            margin-top: 4px;
          }

          .invoice-mobile-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
            margin-top: 12px;
          }

          .invoice-mobile-label {
            display: block;
            font-size: 11px;
            color: #64748b;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            margin-bottom: 3px;
          }

          .invoice-mobile-value {
            font-size: 13px;
            color: #334155;
            word-break: break-word;
          }

          .invoice-mobile-actions {
            margin-top: 12px;
            display: flex;
            justify-content: flex-end;
            gap: 6px;
          }

          .custom-pagination-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
            padding: 16px 0 0;
            border-top: 1px solid #e9ecef;
            margin-top: 16px;
          }

          .custom-pagination-footer .rows-per-page {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .custom-pagination-footer select {
            width: auto;
            padding: 4px 28px 4px 10px;
          }

          .custom-pagination-footer .pagination-info {
            color: #6c757d;
            font-size: 0.85rem;
            white-space: nowrap;
          }

          .custom-pagination-footer .pagination-nav {
            display: flex;
            align-items: center;
            gap: 4px;
            flex-wrap: wrap;
          }

          .custom-pagination-footer .page-btn {
            min-width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #dee2e6;
            background: #fff;
            border-radius: 6px;
            font-size: 0.85rem;
            cursor: pointer;
            color: #343a40;
            padding: 0 8px;
          }

          .custom-pagination-footer .page-btn:hover:not(:disabled) {
            background: #f1f3f5;
          }

          .custom-pagination-footer .page-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
          }

          .custom-pagination-footer .page-btn.active {
            background: #007bff;
            border-color: #007bff;
            color: #fff;
            font-weight: 600;
          }

          .custom-pagination-footer .page-ellipsis {
            padding: 0 4px;
            color: #adb5bd;
          }

          .tooltip {
            font-size: 12px;
            font-weight: 600;
          }

          .tooltip-inner {
            border-radius: 8px;
            padding: 6px 10px;
          }

          @media (max-width: 920px) {
            .invoice-table-scroll {
              display: none;
            }

            .custom-invoice-mobile-list {
              display: block;
            }

            .custom-pagination-footer {
              align-items: stretch;
              flex-direction: column;
            }

            .custom-pagination-footer .pagination-nav {
              justify-content: center;
            }
          }

          @media (max-width: 640px) {
            .custom-invoice-table-card .card-header,
            .custom-invoice-table-card .card-body {
              padding-left: 12px;
              padding-right: 12px;
            }

            .invoice-table-toolbar {
              align-items: stretch;
              flex-direction: column;
            }

            .invoice-search-box {
              width: 100%;
              flex-direction: column;
              align-items: stretch;
            }

            .invoice-mobile-grid {
              grid-template-columns: 1fr;
            }

            .custom-pagination-footer .rows-per-page {
              justify-content: space-between;
            }

            .custom-pagination-footer .pagination-info {
              white-space: normal;
              text-align: center;
            }
          }
        `}
      </style>

      <div className="card basic-data-table custom-invoice-table-card">
        <div className="card-header">
          <div className="invoice-table-toolbar">
            <h5 className="card-title mb-0">Invoices List</h5>

            <div className="invoice-search-box">
              <input
                type="text"
                className="form-control"
                placeholder="Search name, phone, address, invoice no..."
                value={searchTerm}
                onChange={(e) =>
                  onSearchTermChange && onSearchTermChange(e.target.value)
                }
                onKeyDown={handleSearchKeyDown}
              />

              <button
                type="button"
                className="invoice-search-btn"
                onClick={onSearchSubmit}
                disabled={loading}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="invoice-table-scroll">
            <table className="custom-invoice-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Invoice No</th>
                  <th>Customer Name</th>
                  <th>Issued Date</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {jobsData.map((invoice) => {
                  const effectiveStatus = getEffectiveStatus(invoice);

                  return (
                    <tr key={invoice.id} className={getRowClass(invoice)}>
                      <td>
                        <button
                          type="button"
                          className="invoice-id-link"
                          onClick={() =>
                            history(`/invoices/preview/${invoice.id}`)
                          }
                        >
                          {invoice.id}
                        </button>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="invoice-number-link"
                          onClick={() =>
                            history(`/invoices/preview/${invoice.id}`)
                          }
                        >
                          {invoice.invoice_number || "N/A"}
                        </button>
                      </td>

                      <td>
                        <span
                          className="invoice-customer-cell"
                          title={invoice.customer_name}
                        >
                          {invoice.customer_name || "N/A"}
                        </span>
                      </td>

                      <td>
                        <span className="invoice-muted-cell">
                          {formatDateString(invoice.issue_date)}
                        </span>
                      </td>

                      <td>
                        <div className="invoice-date-cell">
                          <span className="invoice-muted-cell">
                            {formatDateString(invoice.due_date)}
                          </span>

                          {effectiveStatus === "overdue" && (
                            <span className="invoice-overdue-date-label">
                              Overdue
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span className="invoice-amount-cell">
                          £{formatMoney(invoice.amount)}
                        </span>
                      </td>

                      <td>
                        <div className="invoice-status-cell">
                          <select
                            className="status-dropdown"
                            value={
                              normalizeStatus(invoice.status) === "paid"
                                ? "paid"
                                : "unpaid"
                            }
                            onChange={(e) =>
                              handleStatusChange(invoice.id, e.target.value)
                            }
                          >
                            <option value="unpaid">Unpaid</option>
                            <option value="paid">Paid</option>
                          </select>

                          <span className={`invoice-status-badge ${effectiveStatus}`}>
                            {effectiveStatus}
                          </span>
                        </div>
                      </td>

                      <td>{renderActions(invoice)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="custom-invoice-mobile-list">
            {jobsData.map((invoice) => {
              const effectiveStatus = getEffectiveStatus(invoice);

              return (
                <div key={invoice.id} className={getMobileCardClass(invoice)}>
                  <div className="invoice-mobile-top">
                    <div>
                      <button
                        type="button"
                        className="invoice-id-link"
                        onClick={() =>
                          history(`/invoices/preview/${invoice.id}`)
                        }
                      >
                        #{invoice.id}
                      </button>

                      <div className="invoice-mobile-title">
                        {invoice.invoice_number || "N/A"}
                      </div>
                    </div>

                    <span className={`invoice-status-badge ${effectiveStatus}`}>
                      {effectiveStatus}
                    </span>
                  </div>

                  <div className="invoice-mobile-grid">
                    <div>
                      <span className="invoice-mobile-label">Customer</span>
                      <span className="invoice-mobile-value">
                        {invoice.customer_name || "N/A"}
                      </span>
                    </div>

                    <div>
                      <span className="invoice-mobile-label">Issue Date</span>
                      <span className="invoice-mobile-value">
                        {formatDateString(invoice.issue_date)}
                      </span>
                    </div>

                    <div>
                      <span className="invoice-mobile-label">Due Date</span>
                      <span className="invoice-mobile-value">
                        {formatDateString(invoice.due_date)}
                      </span>
                    </div>

                    <div>
                      <span className="invoice-mobile-label">Amount</span>
                      <span className="invoice-mobile-value">
                        £{formatMoney(invoice.amount)}
                      </span>
                    </div>

                    <div>
                      <span className="invoice-mobile-label">Status</span>
                      <select
                        className="status-dropdown"
                        value={
                          normalizeStatus(invoice.status) === "paid"
                            ? "paid"
                            : "unpaid"
                        }
                        onChange={(e) =>
                          handleStatusChange(invoice.id, e.target.value)
                        }
                      >
                        <option value="unpaid">Unpaid</option>
                        <option value="paid">Paid</option>
                      </select>
                    </div>
                  </div>

                  <div className="invoice-mobile-actions">
                    {renderActions(invoice)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="custom-pagination-footer">
            <div className="rows-per-page">
              <span className="pagination-info">Rows per page:</span>

              <select
                className="form-select form-select-sm"
                value={limit}
                onChange={(e) => onLimitChange && onLimitChange(Number(e.target.value))}
                disabled={loading}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <span className="pagination-info">
              {total === 0
                ? "No entries"
                : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
            </span>

            <div className="pagination-nav">
              <button
                type="button"
                className="page-btn"
                onClick={() => onPageChange && onPageChange(1)}
                disabled={page <= 1 || loading}
                title="First page"
              >
                <Icon icon="bi:chevron-double-left" />
              </button>

              <button
                type="button"
                className="page-btn"
                onClick={() => onPageChange && onPageChange(page - 1)}
                disabled={page <= 1 || loading}
                title="Previous page"
              >
                <Icon icon="bi:chevron-left" />
              </button>

              {pageNumbers.map((p, index) =>
                p === "..." ? (
                  <span key={`ellipsis-${index}`} className="page-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={`page-btn ${p === page ? "active" : ""}`}
                    onClick={() => onPageChange && onPageChange(p)}
                    disabled={loading}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                type="button"
                className="page-btn"
                onClick={() => onPageChange && onPageChange(page + 1)}
                disabled={page >= totalPages || loading}
                title="Next page"
              >
                <Icon icon="bi:chevron-right" />
              </button>

              <button
                type="button"
                className="page-btn"
                onClick={() => onPageChange && onPageChange(totalPages)}
                disabled={page >= totalPages || loading}
                title="Last page"
              >
                <Icon icon="bi:chevron-double-right" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceListLayer;